import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import { CancelTokenSource } from 'axios';
import CheckBox from 'components/final-form/CheckBox';
import DatePicker from 'components/final-form/DatePicker';
import { DropdownOption, mapToDropdownOptionArray } from 'components/final-form/dropdownUtils';
import Select from 'components/final-form/Select';
import { FormApi, FormState } from 'final-form';
import moment from 'moment';
import React, { useEffect, useState } from "react";
import { Field, Form as FinalForm, FormRenderProps, FormSpy } from 'react-final-form';
import { MessageSourceProps, withMessages } from "react-message-source";
import { Button, Header } from 'semantic-ui-react';
import { getAllGuarantors } from 'services/guarantorServices';
import axios from 'services/http';
import { exportInvoiceStatistic, getInvoiceStatistic } from 'services/invoiceStatisticServices';
import { getAllTariffs } from 'services/tariffServices';
import styled from 'styled-components';
import { InvoiceStatisticData, InvoiceStatisticRequest } from 'ts-types/api.types';
import { noop } from 'utils/functionUtils';

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;

  .ui.buttons {
    margin-left: 10px !important;
  }

  .field {
    display: inline-block !important;
    min-width: 12rem;
  }
`;

const StyledContainer = styled.div`

  flex: 1 1 auto;

  .ui.header:not(h1):not(h2):not(h3):not(h4):not(h5):not(h6) {
    font-size: 1.1rem;
    color: #373737;

    &.small {
      font-size: 1.05em;
    }
  }

  .ui.header.tiny {
    font-size: 1em !important;
  }
`;

const StyledLabel = styled.label`
  display: inline-block;
  min-width: 40px !important;
  margin-right: 10px;

  &.margin + .margin {
    margin-left: 10px;
  }
`;

const SearchOptionsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const SearchGroup = styled.div`
  flex: 0 1 auto;
  margin-left: 5rem;

  &.noMargin {
    margin-left: 0;
  }
`;

const SearchButtonContainer = styled.div`
  flex: 1 1 100%;
`;

const SearchField = styled.div`
  margin-bottom: 7px;
`;

interface Props extends MessageSourceProps, AuthConsumerRenderProps {
  onFilterInvoiceStatisticData: (invoiceStatisticData: InvoiceStatisticData[]) => void,
  onInvoiceStatisticLoadingChange: (dataLoading: boolean) => void,
  setFormDataValues: (formData: InvoiceStatisticRequest) => void,
  setErrorMessage: (errorMessage?: string) => void
}

const InvoiceStatisticOptionsSegment = (props: Props) => {

  const [tariffOptions, setTariffOptions] =
      useState([] as DropdownOption[]);

  const [guarantorsOptions, setGuarantorsOptions] =
      useState([] as DropdownOption[]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const tariffs = await getAllTariffs(cancelTokenSource)
        const tariffOptions = mapToDropdownOptionArray(tariffs, "description");

        const guarantors = await getAllGuarantors(cancelTokenSource);
        const guarantorsOptions = mapToDropdownOptionArray(guarantors, "description");

        setTariffOptions(tariffOptions);
        setGuarantorsOptions(guarantorsOptions);
      } catch (e) {
      }
    }
    fetchData();
    // eslint-disable-next-line
  }, []);

  const {
    onInvoiceStatisticLoadingChange,
    onFilterInvoiceStatisticData,
    setFormDataValues,
    setErrorMessage,
    currentUser,
    getMessage
  } = props;

  const cancelTokenSource: CancelTokenSource = axios.CancelToken.source();

  const initialSearchValues = (): Partial<InvoiceStatisticRequest> => {

    const fromDate = moment().startOf('month').format("YYYY-MM-DD");
    const toDate = moment().endOf('month').format("YYYY-MM-DD");

    return {
      mandateId: currentUser ? currentUser.userConfig.mandateId : undefined,
      fromDate: fromDate,
      toDate: toDate,
      byTreatmentDate: true,
      openByTreatmentDate: true,
      byBillDate: true,
      byPaymentDate: true,
      byStornoDate: true
    }
  };

  const handleChange = ({values}: FormState, form: FormApi) => {
    const fromDate = values.fromDate && new Date(values.fromDate);
    const toDate = values.toDate && new Date(values.toDate);

    if (fromDate && toDate && fromDate.getTime() > toDate.getTime()) {
      form.change("toDate", moment(fromDate).format("YYYY-MM-DD"));
    }
  };

  const fetchInvoiceStatisticData = (values: any, exportToFile?: boolean) => {

    onInvoiceStatisticLoadingChange(true);

    if (exportToFile) {
      exportInvoiceStatistic(values, cancelTokenSource)
          .catch(noop)
          .finally(() => {
            onInvoiceStatisticLoadingChange(false);
          })
    } else {
      getInvoiceStatistic(values, cancelTokenSource)
          .then(response => {
            onFilterInvoiceStatisticData([response]);
            setFormDataValues(values);
          })
          .catch((error) => {
            setErrorMessage(getMessage("error.general"));
          })
          .finally(() => {
            onInvoiceStatisticLoadingChange(false);
          })
    }

  };

  const onEnterKeyPressed = (e: any, values: any) => {
    if (e.keyCode === 13) {
      e.preventDefault();
      fetchInvoiceStatisticData(values)
    }
  };

  const mapSearchFields = () => {
    const fieldNames = ["byTreatmentDate", "openByTreatmentDate", "byBillDate", "byPaymentDate", "byStornoDate"];
    return fieldNames.map(fieldName => (
        <SearchField key={fieldName}>
          <Field
              name={fieldName}
              component={CheckBox}
              label={getMessage(`invoiceStatistic.${fieldName}`)}
          />
        </SearchField>
    ))
  }

  const renderForm = ({handleSubmit, values, form, submitting}: FormRenderProps): React.ReactNode => {


    const disabledBefore = values.fromDate && new Date(values.fromDate);

    const disableSubmit = !values.byTreatmentDate && !values.byBillDate
        && !values.byPaymentDate && !values.byStornoDate;

    return (
        <StyledForm onSubmit={handleSubmit}>
          <StyledContainer>
            <Header>{getMessage("searchOptions")}</Header>
          </StyledContainer>
          <StyledContainer style={{marginTop: 10}}
                           onKeyDown={(e) => onEnterKeyPressed(e, values)}>
            <SearchOptionsContainer>
              <SearchGroup className="noMargin">
                <Header size="tiny">{getMessage("billJournal.timePeriod")}</Header>
                <SearchField>
                  <StyledLabel className="margin">
                    {getMessage("from")}:
                  </StyledLabel>
                  <Field
                      name="fromDate"
                      component={DatePicker}
                      fluid
                  />
                </SearchField>
                <SearchField>
                  <StyledLabel className="margin">
                    {getMessage("to")}:
                  </StyledLabel>
                  <Field
                      name="toDate"
                      component={DatePicker}
                      disabledBefore={disabledBefore}
                      fluid
                  />
                </SearchField>
              </SearchGroup>
              <SearchGroup>
                <Header size="tiny">{getMessage("medicament.selection")}</Header>
                {mapSearchFields()}
              </SearchGroup>
              <SearchGroup>
                <Header size="tiny">{getMessage("tariff")}</Header>
                <SearchField>
                  <Field
                      name="tariffId"
                      component={Select}
                      options={tariffOptions}
                      clearable
                      fluid
                  />
                </SearchField>
              </SearchGroup>
              <SearchGroup>
                <Header size="tiny">{getMessage("searchBy.BILL_GUARANTOR")}</Header>
                <SearchField>
                  <Field
                      name="guarantorId"
                      component={Select}
                      options={guarantorsOptions}
                      clearable
                      fluid
                  />
                </SearchField>
              </SearchGroup>
              <SearchButtonContainer>
                <Button
                    type="button" primary compact
                    onClick={() => fetchInvoiceStatisticData(values)}
                    disabled={disableSubmit}
                >
                  {getMessage("search")}
                </Button>
                <Button
                    type="button"
                    compact
                    disabled={submitting}
                    onClick={() => fetchInvoiceStatisticData(values, true)}
                >
                  {getMessage("interventionAvailability.export")}
                </Button>
              </SearchButtonContainer>
            </SearchOptionsContainer>
          </StyledContainer>
          <FormSpy subscription={{values: true}} onChange={formState => handleChange(formState, form)}/>
        </StyledForm>
    )
  };

  return (
      <FinalForm
          onSubmit={() => noop}
          initialValues={initialSearchValues()}
          subscription={{values: true, pristine: true}}
          render={renderForm}
      />
  );

}

export default withAuthContext(withMessages(InvoiceStatisticOptionsSegment));
